import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { NoyauAttempts } from '@/components/admin/sessions/walk/walk-noyau-attempts';
import {
  type EliminationView,
  FUNCTION_BAR_CLASS,
  PASSAGE_SIDE_CLASS,
  type TextPart,
  conflictSentences,
  discardedSentence,
  eliminationOf,
  eliminationSummary,
  functionBar,
  functionsSummary,
  levelRows,
  levelsSummary,
  noteRows,
  notesSummary,
  noyauNamed,
  rankedAttempts,
  sharesLine,
  styleAbsentSentence,
  styleSummary,
  thresholdsLine,
  weightSentence,
} from '@/lib/session-analysis';
import { drawSentence } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import {
  ANALYST_LEVEL_LABELS,
  type AnalysisStyleNote,
  type NoyauAnalysis,
  type NoyauDetermination,
  noyauOf,
} from '@matter_extractor/shared';
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { Fragment, type ReactNode, useId, useState } from 'react';

/**
 * The REASONING of a determination in two passes, section by section (analyste.md
 * §11.1; maquettes `ParcoursAvantApres` and `ParcoursEtats`): the first pass, the
 * levels, the elimination, the second pass - or the yellow line of a style that
 * could not be read - and the six notes.
 *
 * ## Foldable, as the planches draw them
 *
 * Each section is a button that opens and closes it, and a section that is folded
 * says what it holds in one line (« 6 objectifs lus, trois parts sur 100 chacun »)
 * so the page can be read shut. All five start open - the maquette `ParcoursAvantApres`
 * - except when the style is absent: then the three sections above the yellow
 * line start folded, as `ParcoursEtats` draws them, so the line and the notes it
 * explains are the first thing seen. The state is the block's own, never the
 * URL's: it is a posture of the reader.
 *
 * ## Nothing is derived here
 *
 * Every figure is read off the `analysis` the determination stored, and worded by
 * `lib/session-analysis.ts`; the code that fixed the levels and eliminated the
 * noyaux ran on the server, once, and this screen only says what it decided.
 * The colours of the three functions are the tokens of `styles.css`.
 */

export interface WalkNoyauAnalysisProps {
  readonly determination: NoyauDetermination;
  readonly analysis: NoyauAnalysis;
  /** The mark of one of the six notes: a trial answers marks of its own (`contracts.ts`). */
  readonly scoreTestId: string;
}

export function WalkNoyauAnalysis({
  determination,
  analysis,
  scoreTestId,
}: WalkNoyauAnalysisProps) {
  const absent = analysis.style?.state === 'absent';
  const elimination = eliminationOf(analysis);
  const rows = noteRows(determination, analysis);
  const notes = analysis.style?.state === 'rendered' ? analysis.style.notes : [];
  const conflicts = conflictSentences(analysis);

  return (
    <>
      <Section
        id="passe-1"
        title="Passe 1 · les fonctions par objectif"
        summary={functionsSummary(analysis)}
        open={!absent}
      >
        <FunctionsTable analysis={analysis} />
      </Section>

      <Section
        id="niveaux"
        title="Les niveaux"
        summary={<Parts parts={levelsSummary(analysis)} />}
        open={!absent}
      >
        <LevelsTable analysis={analysis} />
        <p className="text-gray-600 text-xs">
          <Parts
            parts={thresholdsLine(analysis)}
            strongClass="font-semibold text-gray-700 tabular-nums"
          />
        </p>
        {conflicts.map((sentence) => (
          <p
            key={sentence}
            data-testid={SESSIONS_TEST_IDS.walkNoyauConflict}
            className="text-gray-700 text-xs"
          >
            {sentence}
          </p>
        ))}
      </Section>

      <Section
        id="elimination"
        title="L’élimination"
        summary={<Parts parts={eliminationSummary(elimination)} />}
        open={!absent}
      >
        <Elimination view={elimination} />
      </Section>

      {absent ? (
        <div className="flex flex-col gap-2.5 border-gray-100 border-t pt-3.5">
          <div
            role="note"
            data-testid={SESSIONS_TEST_IDS.walkNoyauStyleAbsent}
            className="flex items-start gap-2.5 rounded-md border border-gray-200 border-l-4 border-l-jaune-deep bg-jaune-tint px-3.5 py-2.5"
          >
            <TriangleAlert
              aria-hidden="true"
              className="mt-px size-[18px] shrink-0 text-jaune-deep"
              strokeWidth={2}
            />
            <p className="text-caption text-gray-800 leading-normal">
              {styleAbsentSentence(determination.calls)}
            </p>
          </div>
          {/* The cause of each attempt: the second pass's, the first one was read. */}
          <NoyauAttempts
            attempts={rankedAttempts(determination.calls).filter((attempt) => attempt.pass === 2)}
          />
        </div>
      ) : (
        <Section id="passe-2" title="Passe 2 · le style" summary={styleSummary(analysis)} open>
          <div className="grid gap-4 sm:grid-cols-2">
            {notes.map((note) => (
              <StyleCard key={note.noyau} note={note} />
            ))}
          </div>
        </Section>
      )}

      <Section id="notes" title="Les six notes" summary={notesSummary(rows)} open>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-caption">
            <caption className="sr-only">
              {absent
                ? 'Les six notes, de la compatibilité seule, de la meilleure à la moins bonne'
                : 'Les six notes dérivées des deux passes, de la meilleure à la moins bonne'}
            </caption>
            <thead>
              <tr className="border-gray-200 border-b text-left text-gray-600">
                <Th>Noyau</Th>
                <Th className="w-40">Compatibilité</Th>
                <Th className="w-28">Style</Th>
                <Th className="w-38 pr-0">Note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const noyau = noyauOf(row.noyau);
                return (
                  <tr
                    key={row.noyau}
                    data-testid={scoreTestId}
                    data-noyau={row.noyau}
                    data-place={row.place ?? undefined}
                    data-eliminated={row.eliminated ? 'true' : undefined}
                    className="border-gray-100 border-b align-top last:border-b-0"
                  >
                    <th scope="row" className="whitespace-nowrap py-2.5 pr-3 text-left font-normal">
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="size-2.5 rounded-full"
                          style={{ backgroundColor: noyau.color }}
                        />
                        <span className="font-semibold text-gray-900 text-sm">{noyau.name}</span>
                        <span className="text-gray-600 text-micro">{row.noyau}</span>
                        {row.place === null ? null : (
                          <StatusBadge tone="accent">{row.place}</StatusBadge>
                        )}
                        {row.eliminated ? <StatusBadge tone="neutral">éliminé</StatusBadge> : null}
                      </span>
                    </th>
                    <td className="py-2.5 pr-3 font-semibold text-gray-900 text-sm tabular-nums">
                      {row.compatibility ?? '-'}
                    </td>
                    <td className="py-2.5 pr-3 text-sm tabular-nums">
                      {row.style === null ? (
                        <>
                          <span aria-hidden="true" className="text-gray-600">
                            -
                          </span>
                          <span className="sr-only">
                            {row.eliminated ? 'sans style, éliminé' : 'style absent'}
                          </span>
                        </>
                      ) : (
                        <span className="font-semibold text-gray-900">{row.style}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2.5">
                      <span className="inline-flex items-center gap-2">
                        <b className="w-7 font-bold text-gray-900 text-sm tabular-nums">
                          {row.score}
                        </b>
                        <span
                          aria-hidden="true"
                          className="inline-block h-1.5 w-24 overflow-hidden rounded-pill bg-gray-100"
                        >
                          <span
                            className="block h-full rounded-pill"
                            style={{ width: `${row.score}%`, backgroundColor: noyau.color }}
                          />
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {absent ? null : (
          <p className="text-gray-600 text-xs">{weightSentence(analysis.settings.styleWeight)}</p>
        )}
        {determination.draw === null ? null : (
          <p className="text-caption text-gray-600">{drawSentence(determination.draw)}</p>
        )}
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ pieces */

/** A run of parts of a sentence: the figures and the names in bold. */
function Parts({
  parts,
  strongClass = 'font-semibold text-gray-900 tabular-nums',
}: {
  parts: readonly TextPart[];
  strongClass?: string;
}) {
  return (
    <>
      {parts.map((part, index) =>
        part.strong ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence never move.
          <b key={index} className={strongClass}>
            {part.text}
          </b>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence never move.
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </>
  );
}

function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th scope="col" className={cn('py-1.5 pr-3 font-semibold uppercase tracking-wide', className)}>
      {children}
    </th>
  );
}

/**
 * One section of the block: a heading that is a button, and a body - or, folded,
 * the line that says what the body holds. `open` is where it STARTS.
 */
function Section({
  id,
  title,
  summary,
  open,
  children,
}: {
  id: string;
  title: string;
  summary: ReactNode;
  open: boolean;
  children: ReactNode;
}) {
  const [opened, setOpened] = useState(open);
  const headingId = useId();
  const bodyId = useId();
  const Chevron = opened ? ChevronDown : ChevronRight;
  const heading = (
    <h4 id={headingId}>
      <button
        type="button"
        aria-expanded={opened}
        // Only while the body is there to point at: folded, the line that sums it up stands in.
        aria-controls={opened ? bodyId : undefined}
        data-testid={SESSIONS_TEST_IDS.walkNoyauSectionToggle}
        onClick={() => setOpened((now) => !now)}
        className="inline-flex cursor-pointer items-center gap-1.5 font-semibold text-gray-600 text-micro uppercase tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Chevron aria-hidden="true" className="size-3" strokeWidth={2.5} />
        {title}
      </button>
    </h4>
  );
  return (
    <section
      aria-labelledby={headingId}
      data-testid={SESSIONS_TEST_IDS.walkNoyauSection}
      data-section={id}
      data-open={opened ? 'true' : 'false'}
      className={cn(
        'border-gray-100 border-t',
        opened
          ? 'flex flex-col gap-2.5 pt-3.5'
          : 'flex flex-wrap items-center gap-x-4 gap-y-1 pt-3',
      )}
    >
      {heading}
      {opened ? (
        <div id={bodyId} className="flex flex-col gap-2.5">
          {children}
        </div>
      ) : (
        <p className="text-caption text-gray-600">{summary}</p>
      )}
    </section>
  );
}

/** The first pass: one row per objective, its level, the bar of its three parts, its sentence. */
function FunctionsTable({ analysis }: { analysis: NoyauAnalysis }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-caption">
        <caption className="sr-only">
          Les parts du Souverain, du Guerrier et du Producteur sur 100, objectif par objectif,
          rendues par la passe 1
        </caption>
        <thead>
          <tr className="border-gray-200 border-b text-left text-gray-600">
            <Th className="w-55">Objectif</Th>
            <Th className="w-72">Parts</Th>
            <Th className="pr-0">Justification</Th>
          </tr>
        </thead>
        <tbody>
          {analysis.functions.map((row) => (
            <tr
              key={row.objectiveId}
              data-testid={SESSIONS_TEST_IDS.walkNoyauFunction}
              data-objective-id={row.objectiveId}
              data-level={row.level}
              className="border-gray-100 border-b align-top last:border-b-0"
            >
              <th scope="row" className="whitespace-nowrap py-2.5 pr-3 text-left font-normal">
                <span className="inline-flex items-center gap-2">
                  <span className="font-semibold text-gray-900 text-sm">{row.name}</span>
                  <span className="inline-flex items-center rounded-pill bg-bleu-nuit-tint px-2 font-semibold text-bleu-nuit text-micro leading-[18px]">
                    {ANALYST_LEVEL_LABELS[row.level]}
                  </span>
                </span>
              </th>
              <td className="py-2.5 pr-3">
                <span
                  aria-hidden="true"
                  className="flex h-[18px] w-65 max-w-full overflow-hidden whitespace-nowrap rounded-sm font-bold text-micro text-white leading-[18px]"
                >
                  {functionBar(row.shares).map((segment) => (
                    <span
                      key={segment.fn}
                      style={{ width: `${segment.share}%` }}
                      className={cn('overflow-hidden text-center', FUNCTION_BAR_CLASS[segment.fn])}
                    >
                      {segment.label}
                    </span>
                  ))}
                </span>
                <span className="mt-0.5 block text-gray-600 text-xs tabular-nums">
                  {sharesLine(row.shares)}
                </span>
              </td>
              <td className="py-2.5 text-gray-700">{row.justification}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The three levels: the function each is fixed on, the state and what decided it, the parts the code read. */
function LevelsTable({ analysis }: { analysis: NoyauAnalysis }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-caption">
        <caption className="sr-only">
          Les trois niveaux, fixés ou non par le code à partir des parts de la passe 1
        </caption>
        <thead>
          <tr className="border-gray-200 border-b text-left text-gray-600">
            <Th className="w-55">Niveau</Th>
            <Th className="w-42">Fonction</Th>
            <Th className="w-70">État</Th>
            <Th className="pr-0">Parts lues</Th>
          </tr>
        </thead>
        <tbody>
          {levelRows(analysis).map((row) => (
            <tr
              key={row.level}
              data-testid={SESSIONS_TEST_IDS.walkNoyauLevel}
              data-level={row.level}
              data-state={row.state}
              className="border-gray-100 border-b align-middle last:border-b-0"
            >
              <th scope="row" className="py-2.5 pr-3 text-left font-semibold text-gray-900 text-sm">
                {row.levelLabel}
              </th>
              <td className="py-2.5 pr-3">
                {row.fn === null ? (
                  <>
                    <span aria-hidden="true" className="text-gray-600">
                      -
                    </span>
                    <span className="sr-only">aucune</span>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'inline-flex size-5 items-center justify-center rounded-sm font-bold text-micro text-white leading-none',
                        FUNCTION_BAR_CLASS[row.fn],
                      )}
                    >
                      {row.fn}
                    </span>
                    <span className="font-semibold text-gray-900 text-sm">{row.fnName}</span>
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-3">
                <StatusBadge tone={row.tone}>{row.stateLabel}</StatusBadge>
              </td>
              <td className="py-2.5 text-gray-700 tabular-nums">{row.parts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The candidates the levels left, then the noyaux they struck out, each with its sentence for a reader who has no eyes for a strike-through. */
function Elimination({ view }: { view: EliminationView }) {
  const listId = useId();
  const count = view.candidates.length;
  return (
    <>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-caption text-gray-700">
        <span>
          {count === 1 ? 'Reste' : 'Restent'}{' '}
          <b className="font-bold text-gray-900 tabular-nums">{count}</b>{' '}
          {count === 1 ? 'candidat' : 'candidats'} :
        </span>
        {view.candidates.map((code, index) => (
          <span
            key={code}
            data-testid={SESSIONS_TEST_IDS.walkNoyauCandidate}
            data-noyau={code}
            className="inline-flex items-center gap-2"
          >
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: noyauOf(code).color }}
            />
            <span>
              <b className="font-semibold text-gray-900 text-sm">{noyauNamed(code)}</b>
              {index < count - 1 ? ',' : ''}
            </span>
          </span>
        ))}
      </p>
      {view.eliminated.length === 0 ? (
        <p className="text-caption text-gray-600">
          Aucun noyau n’est éliminé : aucun niveau n’est fixé.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-caption text-gray-700">
          <span id={listId}>Éliminés :</span>
          <ul aria-labelledby={listId} className="flex flex-wrap gap-1.5">
            {view.eliminated.map((entry) => (
              <li key={entry.noyau}>
                <s
                  data-testid={SESSIONS_TEST_IDS.walkNoyauEliminated}
                  data-noyau={entry.noyau}
                  className="inline-block rounded-pill bg-gray-100 px-2 text-gray-600 text-xs leading-5"
                >
                  {entry.noyau}
                  <span className="sr-only"> : {entry.sentence}</span>
                </s>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** One candidate of the second pass: its style, its note, the passages that carry it and those that contradict it. */
function StyleCard({ note }: { note: AnalysisStyleNote }) {
  const noyau = noyauOf(note.noyau);
  const headingId = useId();
  const discarded = discardedSentence(note.discardedCount);
  return (
    <article
      aria-labelledby={headingId}
      data-testid={SESSIONS_TEST_IDS.walkNoyauStyle}
      data-noyau={note.noyau}
      className="flex flex-col gap-2.5 rounded-md border border-gray-200 bg-white p-3.5"
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          aria-hidden="true"
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: noyau.color }}
        />
        <h5 id={headingId} className="font-semibold text-gray-900 text-sm">
          {noyau.name}
        </h5>
        <span className="text-gray-600 text-micro">{note.noyau}</span>
        {note.styleName === '' ? null : (
          <span className="text-caption text-gray-600">
            · style <b className="font-semibold text-gray-900">{note.styleName}</b>
          </span>
        )}
        <span className="ml-auto inline-flex items-baseline gap-0.5 tabular-nums">
          <b className="font-bold text-gray-900 text-lg leading-6">{note.score}</b>
          <span className="text-gray-600 text-xs">/ 100</span>
        </span>
      </header>
      <Passages label="Soutiennent" side="supporting" passages={note.supporting} />
      <Passages label="Contredisent" side="contradicting" passages={note.contradicting} />
      <p className="text-caption text-gray-700 leading-normal">{note.justification}</p>
      {discarded === null ? null : (
        <p data-testid={SESSIONS_TEST_IDS.walkNoyauDiscarded} className="text-gray-600 text-xs">
          {discarded}
        </p>
      )}
    </article>
  );
}

/** The passages of one side, word for word between guillemets; nothing at all when there are none. */
function Passages({
  label,
  side,
  passages,
}: {
  label: string;
  side: keyof typeof PASSAGE_SIDE_CLASS;
  passages: readonly string[];
}) {
  if (passages.length === 0) return null;
  return (
    <div className="flex flex-col gap-1" data-side={side}>
      <p className={cn('font-semibold text-xs', PASSAGE_SIDE_CLASS[side])}>{label}</p>
      <ul className="flex flex-col gap-0.5 text-caption text-gray-900">
        {passages.map((passage, index) => (
          // The rank is in the key: two passages of one note can be the same words.
          <li key={`${index}-${passage}`}>{`« ${passage} »`}</li>
        ))}
      </ul>
    </div>
  );
}
